import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, IconButton, TextField, Tooltip } from '@mui/material';
import { toast } from 'react-toastify';
import {
  PLAYER_NAME_FORBIDDEN_TOAST_DE,
  isPlayerNameForbiddenWordApiError,
} from '../../../../utils/playerNameApiErrors';
import CheckIcon from '@mui/icons-material/Check';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';

const EditableText = ({
  value,
  onSave,
  onSaveAndReport,
  label,
  // Shown instead of an empty value so the click target stays visible for a field
  // that may legitimately be blank (e.g. a member card without a player name).
  emptyText = '',
  disabled = false,
  multiline = false,
  rows = 1,
  variant = 'body1',
  sx = {},
  onEditStart,
  onEditEnd,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const textFieldRef = useRef(null);
  const originalValueRef = useRef(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && textFieldRef.current) {
      textFieldRef.current.focus();
      setTimeout(() => {
        if (textFieldRef.current) {
          const input =
            textFieldRef.current.querySelector('input') ||
            textFieldRef.current.querySelector('textarea');
          if (input) {
            input.select();
          }
        }
      }, 0);
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (editValue !== value) {
      try {
        await onSave(editValue);
      } catch (error) {
        if (isPlayerNameForbiddenWordApiError(error)) {
          toast.error(PLAYER_NAME_FORBIDDEN_TOAST_DE);
        } else {
          toast.error('Fehler beim Speichern');
        }
        console.error('Error saving:', error);
        setEditValue(value);
      }
    }
    setIsEditing(false);
    if (onEditEnd) onEditEnd();
  };

  const handleSaveAndReport = async () => {
    if (!onSaveAndReport) {
      await handleSave();
      return;
    }
    try {
      await onSaveAndReport({
        previousValue: originalValueRef.current,
        nextValue: editValue,
      });
      setIsEditing(false);
      if (onEditEnd) onEditEnd();
    } catch (error) {
      if (isPlayerNameForbiddenWordApiError(error)) {
        toast.error(PLAYER_NAME_FORBIDDEN_TOAST_DE);
      } else {
        toast.error('Fehler beim Speichern');
      }
      console.error('Error saving and reporting:', error);
      setEditValue(value);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
    if (onEditEnd) onEditEnd();
  };

  const handleKeyDown = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSave();
    } else if (event.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = () => {
    handleSave();
  };

  const handleTextClick = () => {
    if (!disabled) {
      originalValueRef.current = value;
      setIsEditing(true);
      if (onEditStart) onEditStart();
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ...sx }}>
        <TextField
          ref={textFieldRef}
          value={editValue}
          onChange={e => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          multiline={multiline}
          rows={rows}
          size='small'
          fullWidth
          variant='outlined'
          sx={{ flex: 1 }}
          {...props}
        />
        {typeof onSaveAndReport === 'function' && (
          <Tooltip title='Bestätigen und melden'>
            <IconButton
              size='small'
              color='warning'
              onMouseDown={e => e.preventDefault()}
              onClick={handleSaveAndReport}
            >
              <FlagOutlinedIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title='Speichern'>
          <IconButton
            size='small'
            color='success'
            onMouseDown={e => e.preventDefault()}
            onClick={handleSave}
          >
            <CheckIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        cursor: disabled ? 'default' : 'pointer',
        '&:hover': {
          backgroundColor: disabled ? 'transparent' : 'rgba(0, 0, 0, 0.04)',
          borderRadius: 1,
        },
        ...sx,
      }}
      onClick={handleTextClick}
    >
      <Typography
        variant={variant}
        sx={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >
        {label && <b>{label}:</b>} {value || emptyText}
      </Typography>
    </Box>
  );
};

export default EditableText;
